/**
 * Simplified bonus calculator for qubit harvesting
 */
export class BonusCalculator {
  /**
   * Calculate total multiplier from all sources
   * @param {Object} analysisData Complete quantum analysis data
   * @returns {Object} Multiplier info including total and individual bonuses
   */
  static calculateMultipliers(analysisData) {
    const {
      signalSummary,
      quantumState,
      rewards,
      particleActivity,
      cosmicEvents
    } = analysisData;

    const bonuses = [];
    let totalMultiplier = 1;

    // Signal Quality Bonus (0-50%)
    const qualityBonus = (parseFloat(signalSummary?.quality) || 0) / 200;
    if (qualityBonus > 0.1) {
      bonuses.push({
        type: 'Signal Quality',
        multiplier: (1 + qualityBonus).toFixed(2) + 'x',
        quality: signalSummary.quality + '%'
      });
      totalMultiplier *= (1 + qualityBonus);
    }

    // Quantum State Bonus (based on coherence and entanglement)
    const coherence = parseFloat(quantumState?.coherence) || 0;
    const entanglement = parseFloat(quantumState?.entanglement) || 0;
    const quantumBonus = (coherence + entanglement) / 400; // Average divided by 200
    if (quantumBonus > 0.1) {
      bonuses.push({
        type: 'Quantum State',
        multiplier: (1 + quantumBonus).toFixed(2) + 'x',
        quality: Math.round((coherence + entanglement) / 2) + '%'
      });
      totalMultiplier *= (1 + quantumBonus);
    }

    // Rarity Bonus
    const rarityBonus = this.calculateRarityBonus(rewards?.rarity);
    if (rarityBonus > 0) {
      bonuses.push({
        type: `${rewards.rarity} Signal`,
        multiplier: (1 + rarityBonus).toFixed(2) + 'x',
        quality: signalSummary.quality + '%'
      });
      totalMultiplier *= (1 + rarityBonus);
    }

    // Particle Activity Bonus (simple percentage)
    if (particleActivity) {
      const particleBonus = parseFloat(particleActivity.confidence) / 200;
      if (particleBonus > 0.1) {
        bonuses.push({
          type: 'Particle Activity',
          multiplier: (1 + particleBonus).toFixed(2) + 'x',
          quality: particleActivity.confidence + '%'
        });
        totalMultiplier *= (1 + particleBonus);
      }
    }

    // Cosmic Events Bonus (simple percentage)
    if (cosmicEvents) {
      const cosmicBonus = parseFloat(cosmicEvents.significance) / 200;
      if (cosmicBonus > 0.1) {
        bonuses.push({
          type: 'Cosmic Events',
          multiplier: (1 + cosmicBonus).toFixed(2) + 'x',
          quality: cosmicEvents.significance + '%'
        });
        totalMultiplier *= (1 + cosmicBonus);
      }
    }

    // Cap the total multiplier
    totalMultiplier = Math.min(Math.max(totalMultiplier, 1), 5);

    return {
      total: totalMultiplier,
      bonuses
    };
  }

  /**
   * Calculate rarity bonus with simplified values
   * @param {string} rarity Signal rarity level
   * @returns {number} Rarity bonus multiplier
   */
  static calculateRarityBonus(rarity) {
    const bonusMap = {
      'LEGENDARY': 0.5,  // +50% bonus
      'EPIC': 0.4,      // +40% bonus
      'RARE': 0.3,      // +30% bonus
      'UNCOMMON': 0.2,  // +20% bonus
      'COMMON': 0.1     // +10% bonus
    };
    return bonusMap[rarity] || 0;
  }
}